import Dropdown, { Option } from "@components/form/Dropdown.component";
import { BaseModel } from "@models/orm";
import PageStructure, { PageContent, PageContentHeader, PageContentInner, PageContentNoSidebar, PageSidebar, Pane, PaneContent, PaneContentWithSubnav } from "@pages/PageStructure.component";
import { shortid } from "@services/id.service";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Form, Modal, Offcanvas } from "react-bootstrap";
import GridLayout, { Layout } from "react-grid-layout";
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import styled from 'styled-components';
import { useImmer } from "use-immer";
import Markdown from 'react-markdown'
import { requireConfirmation } from "@services/alert/alert.service";
import PipelineNodeSelector from "@components/pipelineNodes/PipelineNodeSelector.component";
import PipelineNodeDataTable from "@components/pipelineNodes/PIpelineNodeDataTable";
import VisualizationSelector from "@components/pipelineNodes/VisualizationSelector.component";
import VisualizationChart from "@components/pipelineNodes/VisualizationChart.component";
import { DashboardComponent, Dashboard, DashboardFilterOption, DashboardObjectType, useDashboard, DashboardORM, DashboardDateRange } from "@models/dashboard";
import DashboardFilterOptionForm, { FilterForm, FiltersByNodeId } from "./DashboardFilterOptionForm.component";
import { queryClient, toggleFavoriteDashboard, useIsFavoriteDashboard, useIsInDraftMode, usePipelineNodes } from "@stores/data.store";
import { useVisualizations } from "@models/visualization";
import { FilterConfig, PipelineNode } from "@models/pipelineNode";
import { RecordInfoStyles } from "@components/pipelineNodes/PipelineNodeInfo.component";
import RecordTitleAndDescription from "@components/card/RecordTitleAndDescription.component";
import { useNavigate, useParams } from "react-router-dom";
import toast from "@services/toast.service";
import { getErrorMessage } from "@services/errors.service";
import SaveButton from "@components/button/SaveButton.component";
import { DraftOnly, ReadOnly, ProdOnly } from "@components/project/DraftModeRequired.component";
import PipelineNodeColumnSelector, { PipelineNodeMultiColumnSelector } from "@components/pipelineNodes/PipelineNodeColumnSelector.component";
import { Analysis, AnalysisDimensionFilter, AnalysisORM, useAnalyses } from "@models/analysis";
import AnalysisVisualization from './AnalysisVisualization.component';
import BuildModal from "@services/alert/BuildModal.component";
import EditableText from "@components/general/EditableText.component";
import { convertToTitleCase } from '@services/formatting.service';
import DateRangeSelector from "@components/form/DateRangeSelector.component";
import FavoriteButton from '@components/button/FavoriteButton.component';
import { ReportBuilderDimension, ReportBuilderMeasure, useDimensions, useMeasures } from "@models/reportBuilder";
import Warning from "@components/statusIndicators/Warning.component";
import produce from "immer";
import { Dropdown as BSDropdown } from "react-bootstrap";
import { DashboardNotificationsEditor } from "@components/notifications/DashboardNotifications.component";
import { useQueryParams } from "@services/url.service";




interface ComponentFormProps {
    component: DashboardComponent;
    onSave: (c: DashboardComponent) => any;
    onDelete: () => any;
    filterColumnOptions: Option[];
}

const ComponentForm = (props: ComponentFormProps) => {
    const addNewFilterOption = useCallback(() => {

    }, []);
    const [editingComponent, setEditingComponent] = useImmer<DashboardComponent>({
        id: '',
        title: '',
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        object_id: '',
    });

    const analyses = useAnalyses();

    const analysisOptions = useMemo(() => {
        if (!analyses.data) return [];
        return analyses.data.map(analysis => ({
            value: analysis.id as string,
            label: analysis.name,
            description: analysis.description,
            badgeText: convertToTitleCase(analysis.visualization_type)
        }));
    }, [analyses.data]);

    useEffect(() => {
        setEditingComponent({...props.component})
    }, [props.component]);




    return <ReadOnly>
        <Form.Group className="mb-2">
            <Form.Label>Title</Form.Label>
            <Form.Control onChange={(e) => {
                setEditingComponent(draft => {
                    draft.title = e.target.value as string;
                })
            }} value={editingComponent.title}/>
        </Form.Group>
        <Form.Group className="mb-2">
            <Form.Label>Component Type</Form.Label>
            <Dropdown
                options={[{
                    value: 'NODE',
                    label: 'Node',
                    description: "Show data from a specific node",
                }, {
                    value: 'ANALYSIS',
                    label: 'Chart',
                    description: 'Show a chart',
                }, {
                    value: 'MARKDOWN',
                    label: 'Markdown',
                    description: 'Write arbitrary markdown content',
                }, {
                    value: 'FILTER',
                    label: 'Filter',
                    description: 'Filter other components'
                }]}
                selected={editingComponent.object_type}
                onChange={(newVal: string) => {
                    setEditingComponent(draft => {
                        draft.object_type = newVal as DashboardObjectType;
                    })
                }}
            />
        </Form.Group>
        {editingComponent.object_type === 'ANALYSIS' && <>
            <Form.Group className="mb-2">
                <Form.Label>Select Chart</Form.Label>
                <Dropdown
                    options={analysisOptions}
                    selected={editingComponent.object_id}
                    onChange={(newVal: string) => {
                        setEditingComponent(draft => {
                            draft.object_id = newVal;
                        })
                    }}
                />
            </Form.Group>
            <Form.Group className="mb-2">
                <Form.Check
                    type="switch"
                    checked={!!editingComponent.ignore_date_range}
                    onChange={(e) => {
                        setEditingComponent(draft => {
                            draft.ignore_date_range = e.target.checked;
                        })
                    }}
                    label="Ignore date range"
                />
                <Form.Text>
                    If enabled, this component will not be affected by the date range selected at the top of the page.
                </Form.Text>
            </Form.Group>
        </>}
        {editingComponent.object_type === 'MARKDOWN' && <>
            <Form.Group className="mb-2">
                <Form.Label>Markdown Content</Form.Label>
                <Form.Control 
                    as="textarea" 
                    onChange={(e) => {
                        setEditingComponent(draft => {
                            draft.markdown_content = e.target.value as string;
                        })
                    }} 
                    value={editingComponent.markdown_content}
                />
            </Form.Group>
        </>}
        {editingComponent.object_type === 'NODE' && <>
            <Form.Group className="mb-2">
                <Form.Label>Select Node</Form.Label>
                <PipelineNodeSelector
                    selectedId={editingComponent.object_id}
                    onSelect={(pn) => {
                        setEditingComponent(draft => {
                            if (pn) {
                                draft.object_id = pn.id as string;
                            } else {
                                draft.object_id = '';
                            }
                        })
                    }}
                />
               
            </Form.Group>
            {!!editingComponent.object_id && <>
                <Form.Group className="mb-2">
                    <Form.Label className="small">Select column(s) to show</Form.Label>
                    <PipelineNodeMultiColumnSelector
                        pipelineNodeId={editingComponent.object_id}
                        selectedIds={editingComponent.column_whitelist || []}
                        onSelect={(columnIds) => {
                            setEditingComponent(draft => {
                                draft.column_whitelist = columnIds || [];
                            })
                        }}
                    />
                </Form.Group>
                    

            </>}
            
        </>}
        {editingComponent.object_type === 'VISUALIZATION' && <>
            <Form.Group className="mb-2">
                <Form.Label>Select Visualization</Form.Label>
                <VisualizationSelector
                    selectedId={editingComponent.object_id}
                    onSelect={(pn) => {
                        setEditingComponent(draft => {
                            if (pn) {
                                draft.object_id = pn.id as string;
                            } else {
                                draft.object_id = '';
                            }
                        })
                    }}
                />
            </Form.Group>
            
        </>}
        {editingComponent.object_type === 'FILTER' && <>
            <DashboardFilterOptionForm
                option={editingComponent.filter_config || {
                    column_references: [],
                    filter_type: 'DIMENSION',
                    comparator: '=',
                }}
                onChange={(newData) => {
                    setEditingComponent(draft => {
                        draft.filter_config = newData;
                    })
                }}
                columnOptions={props.filterColumnOptions}
            />
        </>}
        
        <hr />
        <div>
            <button className="btn btn-outline-primary me-1" onClick={() => {
                props.onSave(editingComponent)
            }}>
                <i className="mdi mdi-check"></i> Apply Changes
            </button>
            <button className="btn btn-outline-secondary me-1" onClick={() => {
                props.onSave(props.component);
            }}>
                <i className="mdi mdi-undo"></i> Cancel
            </button>
            <button className="btn btn-outline-danger" onClick={() => {
                props.onDelete()
            }}>
                <i className="mdi mdi-delete"></i> Delete Component
            </button>
        </div>
        
    </ReadOnly>
}

const GridItemContainerStyles = styled.div`
    width: 100%;
    height: 100%;
    background: white;
    border: solid 1px var(--ct-border-color);
    border-radius: 5px;
    position: relative;
    display: flex;
    flex-direction: column;

    div.buttons {
        position: relative;
        z-index: 1;

        button {
            margin-left: .25rem;
        }
    }

    .drag {
        cursor: move;
    }

    button.drag {
        position: absolute;
    }

    .header {
        padding: .5rem;
        flex: 0 0 auto;
    }

    .component-content {
        flex: 1 1 auto;
        min-height: 0; /* This is crucial for Firefox */
        margin: .5rem;
        position: relative;
        overflow: auto;
    }

    .component-content > div {
        position: absolute;
        inset: 0;
        overflow: auto;
    }

    /* Special styling for no-selection messages */
    .component-content .text-center {
        position: static;
        height: auto;
        min-height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 1rem;
    }
`;



interface GridItemContainerProps {
    component: DashboardComponent;
    onEdit: () => any;
    onDelete: () => any;
    filters?: FilterConfig;
    isReady: boolean;
    mode?: string;
    onChangeFilters?: (newData: FiltersByNodeId) => any;
    loadingDimensionFilters?: boolean;
    onChangeDimensionFilters?: (newFilters: AnalysisDimensionFilter[]) => any;
    onChangeDateRange?: (newDateRange: DashboardDateRange) => any;
    dateRangeConfig?: DashboardDateRange;
    dimensionFilters?: AnalysisDimensionFilter[];
    errors?: string[];
    onShowErrors?: () => any;
}

const GridItemContainer = (props: GridItemContainerProps) => {
    const navigate = useNavigate();    
    const initEdit = useCallback((e: any) => {
        e.preventDefault();
        e.stopPropagation();
        props.onEdit();
    }, [props.onEdit]);

    const runDelete = useCallback((e: any) => {
        e.preventDefault();
        e.stopPropagation();
        props.onDelete();
    }, [props.onDelete]);

    const navigateToSource = useCallback((e: any) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (props.component.object_type === 'ANALYSIS') {
            const params = new URLSearchParams();
            
            // Add date range parameters if they exist and component is not set to ignore date range
            if (props.dateRangeConfig && !props.component.ignore_date_range) {
                params.set('date_range', props.dateRangeConfig.date_range || '');
                if (props.dateRangeConfig.custom_date_range_start) {
                    params.set('custom_date_range_start', props.dateRangeConfig.custom_date_range_start);
                }
                if (props.dateRangeConfig.custom_date_range_end) {
                    params.set('custom_date_range_end', props.dateRangeConfig.custom_date_range_end);
                }
            }
            if (props.dimensionFilters && props.dimensionFilters.length > 0) {
                const nonEmptyFilters = props.dimensionFilters.filter(filter => 
                    filter.value && (!Array.isArray(filter.value) || filter.value.length > 0)
                );
            
                if (nonEmptyFilters.length > 0) {
                    params.set('dimension_filters', JSON.stringify(nonEmptyFilters));
                }
            }

            if (!!props.mode) {
                params.set('mode', props.mode);
            }

            const queryString = params.toString();
            navigate(`/charts/${props.component.object_id}${queryString ? `?${queryString}` : ''}`);
        } else if (props.component.object_type === 'NODE') {
            navigate(`/node/${props.component.object_id}`);
        }
    }, [props.component.object_type, props.component.object_id, props.dateRangeConfig, props.dimensionFilters, props.component.ignore_date_range, navigate]);

    const showNavigateButton = props.component.object_type === 'ANALYSIS' || props.component.object_type === 'NODE';
    
    const renderComponent = () => {
        if(props.component.object_type == 'MARKDOWN') {
            return <Markdown>{props.component.markdown_content}</Markdown>
        } else if(props.component.object_type == 'FILTER') {
            return <FilterForm 
                filter_option={props.component.filter_config!}
                onChange={(newFilters, newDimensionFilters?: AnalysisDimensionFilter[]) => {
                    props.onChangeFilters && props.onChangeFilters(newFilters);

                    if (newDimensionFilters) {
                        props.onChangeDimensionFilters && props.onChangeDimensionFilters(newDimensionFilters);
                    }
                }} 
            />
        } else if(props.component.object_type == 'NODE') {
            return <PipelineNodeDataTable
                pipelineNodeId={props.component.object_id}
                compact
                filters={props.filters}
                columnWhitelist={props.component.column_whitelist}
                excludeRelationships
                onEdit={props.onEdit}
            />
        } else if(props.component.object_type == 'ANALYSIS') {
            // Because of the way analyses work, filters are built 
            // into the analysis rather than getting passed in when changed.

            return <AnalysisVisualization
                dimensionFilters={props.dimensionFilters}
                analysisId={props.component.object_id}
                loadingDimensionFilters={props.loadingDimensionFilters}
                staticFilters={props.filters}
                dateRange={props.component.ignore_date_range ? undefined : props.dateRangeConfig}
                onEdit={props.onEdit}
            />
        } else if(props.component.object_type == 'VISUALIZATION') {
            return <VisualizationChart
                visualizationId={props.component.object_id}
                compact
                filter={props.filters}
                vizHeight={`${30*props.component.height}px`}
            />
        }
    };

    const showErrors = useCallback(() => {
        if (props.onShowErrors) {
            props.onShowErrors();
        }
    }, [props.onShowErrors])

    return <GridItemContainerStyles>
        <div className="d-flex center-vertically header">
            <DraftOnly>
                <button className="icon-button font-24"><i className="mdi mdi-drag drag"></i></button>

            </DraftOnly>
            <h4 className="mb-0 flex-1">
                {props.component.title}
            </h4>
            {props.errors && props.errors.length > 0 && <button className="icon-button me-1">
                <i className="mdi mdi-alert text-danger font-18" onClick={showErrors}></i>
            </button>}
            {/* Show warning icon if component type is ANALYSIS but no chart is selected */}
            {props.component.object_type === 'ANALYSIS' && !props.component.object_id && (
                <button className="icon-button me-1 font-18" title="No chart selected">
                    <i className="mdi mdi-alert-circle text-warning"></i>
                </button>
            )}
            {/* Show warning icon if component type is NODE but no node is selected */}
            {props.component.object_type === 'NODE' && !props.component.object_id && (
                <button className="icon-button me-1 font-18" title="No node selected">
                    <i className="mdi mdi-alert-circle text-warning"></i>
                </button>
            )}
            {showNavigateButton && (
                <button className="icon-button me-1 font-18" title="Analyze from here" onClick={navigateToSource} data-testid={`afh_nav-${props.component.id}`}>
                    <i className="mdi mdi-share-circle"></i>
                </button>
            )}
            
            <DraftOnly>
                <button className="icon-button btn-sm me-1 font-18" title="Edit Component" onClick={initEdit}>
                    <i className="mdi mdi-pencil-circle"></i>
                </button>
                <button className="icon-button font-18" onClick={runDelete}>
                    <i className="mdi mdi-delete"></i>
                </button>
            </DraftOnly>
            
        </div>
        <div className="component-content">
            <div className="h-100 w-100">{renderComponent()}</div>
            
        </div>
    </GridItemContainerStyles>
}

interface Tree {
    [key: string]: string[];
};

const getUpstreamTerminators = (tree: Tree, id: string) => {
    const terminators: string[] = [];
    const node = tree[id];

    if (!node) {
        return terminators;
    }

    if (node.length === 0) {
        terminators.push(id);
    } else {
        node.forEach(n => {
            terminators.push(...getUpstreamTerminators(tree, n));
        });
    }

    return terminators;

}

const DashboardEditorPage = () => {
    const { dashboardId } = useParams();
    const inDraftMode = useIsInDraftMode();
    // isReady to Load Data/Visualizations (aka all filters have been set)
    const [isReady, setIsReady] = useState(false);

    const ogDashboard = useDashboard(dashboardId as string);
    const isNew = !dashboardId;

    const [dashboard, setDashboard] = useImmer<Dashboard>({
        id: null,
        components: [],
        name: '',
        description: ''
    });

    const analyses = useAnalyses();

    const isFavorite = useIsFavoriteDashboard(dashboard.id as string);


    const searchParams = useQueryParams();

    const mode: string = searchParams.get('mode') || 'view';

    useEffect(() => {
        if (ogDashboard.data) {
            setDashboard(ogDashboard.data);
        }
    }, [ogDashboard.dataUpdatedAt]);

    // Initialize dimension filters from filter component default values
    useEffect(() => {
        if (!ogDashboard.data) {
            return;
        }
        if (ogDashboard.data.components && ogDashboard.data.components.length > 0) {
            const initialDimensionFilters: AnalysisDimensionFilter[] = [];
            
            ogDashboard.data.components.forEach(component => {
                if (component.object_type === 'FILTER' && 
                    component.filter_config && 
                    component.filter_config.filter_type === 'DIMENSION' &&
                    component.filter_config.dimension_id &&
                    component.filter_config.default_value) {
                    
                    const defaultValue = Array.isArray(component.filter_config.default_value)
                        ? component.filter_config.default_value
                        : [component.filter_config.default_value];
                    
                    initialDimensionFilters.push({
                        dimension_id: component.filter_config.dimension_id,
                        comparator: 'IN',
                        value: defaultValue,
                    });
                }
            });
            
            if (initialDimensionFilters.length > 0) {
                setDimensionFilters(initialDimensionFilters);
                setDimensionFiltersForViz(initialDimensionFilters);
            }
        }
    }, [ogDashboard.dataUpdatedAt]);

    const addItem = useCallback(() => {
        const id = shortid();
        const newComponent = {
            id: id,
            x: 0,
            y: 0,
            width: 4,
            height: 4,
            title: 'New Component',
            object_id: '',
        };
        setDashboard(draft => {
            draft.components.push(newComponent)
        });
        editComponent(newComponent);
    }, []);

    const [filtersByComponentId, setFiltersByComponentId] = useImmer<{
        [componentId: string]: FilterConfig
    }>({});

    const [activeComponent, setActiveComponent] = useState<DashboardComponent|undefined>(undefined);

    const [showDrawer, setShowDrawer] = useState(false);

    const editComponent = useCallback((c: DashboardComponent) => {
        setActiveComponent(c);
        setShowDrawer(true);
    }, []);

    const saveActiveComponent = useCallback((newData: DashboardComponent) => {
        if (!activeComponent) {
            return;
        }
        setDashboard(draft => {
            const idx = draft.components.findIndex(c => c.id === activeComponent.id);
            if (idx >= 0) {
                draft.components[idx] = newData;
            }
        });
        setActiveComponent(undefined);
        setShowDrawer(false);
    }, [activeComponent]);

    const deleteComponent = useCallback(async (componentId: string) => {
        const confirmed = await requireConfirmation('Are you sure you want to delete this component?', 'Delete Component', 'Delete');
        if (!confirmed) {
            return;
        }
        const componentToDelete = dashboard.components.find(c => c.id === componentId);
        let filterDimensionId: any = null;
        if (componentToDelete && componentToDelete.object_type === 'FILTER' && componentToDelete.filter_config) {
            filterDimensionId = componentToDelete.filter_config.dimension_id;
        }
        setDashboard(draft => {
            const idx = draft.components.findIndex(c => c.id === componentId);
            if (idx >= 0) {
                draft.components.splice(idx, 1);
            }
        });
        if (filterDimensionId) {
            setDimensionFilters(draft => {
                return draft.filter(filter => filter.dimension_id !== filterDimensionId);
            });
        }
        setShowDrawer(false);
        setActiveComponent(undefined);
    }, [dashboard.components, setDashboard]);

    const handleLayoutChange = useCallback((layout: Layout[]) => {
        setDashboard(draft => {
            if (layout.length != draft.components.length) {
                throw new Error('Unexpected layout change')
            }

            draft.components.forEach((component, idx) => {
                component.height = layout[idx].h;
                component.width = layout[idx].w;
                component.x = layout[idx].x;
                component.y = layout[idx].y;
            })
        });
    }, []);

    const pipelineNodes = usePipelineNodes();
    const visualizations = useVisualizations();
    const measures = useMeasures();
    const dimensions = useDimensions();

    const dimensionsById = useMemo(() => {
        if (!dimensions.data) {
            return {};
        }
        return dimensions.data.reduce((acc, d) => {
            acc[d.id as string] = d;
            return acc;
        }, {} as {[key: string]: ReportBuilderDimension});
    }, [dimensions.dataUpdatedAt]);

    const dimensionIdsByPipelineNodeId = useMemo(() => {
        if (!dimensions.data) {
            return {};
        }
        return dimensions.data.reduce((acc, d) => {
            if (!(d.pipeline_node_id in acc)) {
                acc[d.pipeline_node_id] = [];
            }
            acc[d.pipeline_node_id].push(d.id as string);
            return acc;
        }, {} as {[key: string]: string[]});
    }, [dimensions.dataUpdatedAt]);

    const measuresById = useMemo(() => {
        if (!measures.data) {
            return {};
        }
        return measures.data.reduce((acc, d) => {
            acc[d.id as string] = d;
            return acc;
        }, {} as {[key: string]: ReportBuilderMeasure});
    }, [measures.dataUpdatedAt]);

    const measureDependencyTree = useMemo(() => {
        if (!measures.data) {
            return {};
        }
        const tree: Tree = {};
        measures.data.forEach(m => {
            tree[m.id as string] = m.parent_measure_ids || [];
        });
        return tree;
    }, [measures.dataUpdatedAt]);

    const filterColumnOptions = useMemo(() => {
        if (!pipelineNodes.data || !visualizations.data || !analyses.data) {
            return [];
        }
        const pipelineNodeIdsToInclude = new Set<string>([]);

        const visualizationPipelineNodes: {
            [id: string]: string;
        } = {};
        visualizations.data.forEach(v => {
            visualizationPipelineNodes[v.id as string] = v.pipeline_node_id;
        });

        const staticAnalysisPipelineNodes: {
            [id: string]: string;
        } = {};
        analyses.data.forEach(a => {
            if (a.data_source_type === 'STATIC') {
                staticAnalysisPipelineNodes[a.id as string] = a.static_source_pipeline_node_id || '';
            }
        });

        dashboard.components.forEach(c => {
            let pnId: string;
            switch (c.object_type) {
                case 'NODE':
                    pipelineNodeIdsToInclude.add(c.object_id);
                    break;
                case 'VISUALIZATION':
                    pnId = visualizationPipelineNodes[c.object_id];
                    if (pnId) {
                        pipelineNodeIdsToInclude.add(pnId);
                    }
                    break;
                case 'ANALYSIS':
                    pnId = staticAnalysisPipelineNodes[c.object_id];
                    if (pnId) {
                        pipelineNodeIdsToInclude.add(pnId);
                    }
                    break;
            }
        });

        const options: Option[] = [];
        pipelineNodes.data.forEach(pn => {
            if (pipelineNodeIdsToInclude.has(pn.id as string)) {
                pn.fields.forEach(f => {
                    options.push({
                        label: `${pn.name}.${f.name}`,
                        value: `${pn.id}.${f.id}`,
                        description: f.description,
                    })
                })
            }
        });

        return options;
    }, [pipelineNodes.dataUpdatedAt, visualizations.dataUpdatedAt, dashboard.components.map(c => `${c.object_type}:${c.object_id}`)]);

    const [filtersByFilterComponentId, setFiltersByFilterComponentId] = useImmer<{
        [key: string]: FiltersByNodeId
    }>({});

    const [dirtyFilters, setDirtyFilters] = useState(false);

    const [needsRefresh, setNeedsRefresh] = useState(true); // New state variable, defaulted to true

    const [dateRangeConfig, setDateRangeConfig] = useState<DashboardDateRange | undefined>({
        date_range: 'ALL_TIME',
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    // New state variable for the applied date range
    const [appliedDateRange, setAppliedDateRange] = useState<DashboardDateRange | undefined>();


    useEffect(() => {
        if (!visualizations.data || !analyses.data) {
            return;
        }
        if (dirtyFilters) {
            const tmpFiltersByNodeId: FiltersByNodeId = {}
            Object.keys(filtersByFilterComponentId).forEach(k => {
                Object.keys(filtersByFilterComponentId[k]).forEach(k1 => {
                    if (!(k1 in tmpFiltersByNodeId)) {
                        tmpFiltersByNodeId[k1] = [];
                    }
                    filtersByFilterComponentId[k][k1].forEach(f => {
                        tmpFiltersByNodeId[k1].push(f);
                    })
                })
            });


            // Now loop through each component so we can grab the filters
            // for that node even if it's a visualization
            const visualizationPipelineNodes: {
                [id: string]: string;
            } = {};
            visualizations.data.forEach(v => {
                visualizationPipelineNodes[v.id as string] = v.pipeline_node_id;
            });

            const staticAnalysisPipelineNodes: {
                [id: string]: string;
            } = {};
            analyses.data.forEach(a => {
                if (a.data_source_type === 'STATIC') {
                    staticAnalysisPipelineNodes[a.id as string] = a.static_source_pipeline_node_id || '';
                }
            });

            setFiltersByComponentId(draft => {
                dashboard.components.forEach(c => {
                    if (c.object_type == 'NODE') {
                        if (c.object_id in tmpFiltersByNodeId) {
                            draft[c.id] = {
                                filters: tmpFiltersByNodeId[c.object_id],
                                logic_gate: 'AND',
                            };
                        }
                    } else if (c.object_type == 'VISUALIZATION') {
                        const pnId = visualizationPipelineNodes[c.object_id];
                        if (pnId in tmpFiltersByNodeId) {
                            draft[c.id] = {
                                filters: tmpFiltersByNodeId[pnId],
                                logic_gate: 'AND',
                            };
                        }
                    } else if (c.object_type == 'ANALYSIS') {
                        const pnId = staticAnalysisPipelineNodes[c.object_id];
                        if (pnId in tmpFiltersByNodeId) {
                            draft[c.id] = {
                                filters: tmpFiltersByNodeId[pnId],
                                logic_gate: 'AND',
                            };
                        }

                    }
                });
            })

            

            setDirtyFilters(false);
        }
    }, [dirtyFilters, analyses.dataUpdatedAt]);

    useEffect(() => {
        if (!dashboard.components || dashboard.components.length === 0) {
            return;
        }
        const filtersOnly = dashboard.components.filter(c => c.object_type === 'FILTER');
        if(filtersOnly.length > 0 && Object.keys(filtersByComponentId).length === 0) {
            // if we have filters and they havent been set, lets wait before we load data/visualizations
            setIsReady(false);
        }else{
            setIsReady(true);
        }
    }, [dashboard.components, filtersByComponentId]);

    const handleFilterChange = useCallback((componentId: string, newFilters: FiltersByNodeId) => {
        setFiltersByFilterComponentId(draft => {
            draft[componentId] = newFilters;
        });
        setNeedsRefresh(true);
    }, []);

    const changeName = useCallback(async (newName: string) => {
        setDashboard(draft => {
            draft.name = newName;
        });

    }, []);

    const changeDescription = useCallback(async (newDescription: string) => {
        setDashboard(draft => {
            draft.description = newDescription;
        });
       
    }, []);

    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    const save = useCallback(async () => {
        setSaving(true);
        try {
            const savedDashboard = await DashboardORM.save({
                ...dashboard,
                date_range_config: dateRangeConfig
            });
            ogDashboard.refetch();
            
            // If this is a new dashboard (no ID), redirect to the saved dashboard's URL
            if (!dashboard.id) {
                navigate(`/dashboards/${savedDashboard.id}`);
            }
        } catch (err) {
            toast('danger', 'Error', getErrorMessage(err));
        } finally {
            setSaving(false);
        }
    }, [dashboard, dateRangeConfig, navigate]);

    useEffect(() => {
        if (ogDashboard.data?.date_range_config) {
            setDateRangeConfig(ogDashboard.data.date_range_config);
            // Also set the applied date range to match the loaded config
            setAppliedDateRange(ogDashboard.data.date_range_config);
        }
    }, [ogDashboard.data]);

    const deleteDashboard = useCallback(async () => {
        const confirm = await requireConfirmation(`Are you sure you want to delete this dashboard? Please type the name of the dashboard ("${dashboard.name}") to continue`, 'Confirm Deletion', 'Delete', 'Cancel', dashboard.name);
        if (confirm) {
            await DashboardORM.deleteById(dashboard.id as string);
            navigate('/');
            toast('success', 'Success', 'Dashboard deleted');
        }
    }, [dashboard.name]);

    const [dimensionFilters, setDimensionFilters] = useImmer<AnalysisDimensionFilter[]>([]);

    // We set this from the dimensionFilters when somebody hits refresh, so it doesn't automatically try to load the data whenever it changes.
    const [dimensionFiltersForViz, setDimensionFiltersForViz] = useImmer<AnalysisDimensionFilter[]>([]);

    const [updatingDimensionFilters, setUpdatingDimensionFilters] = useState(false);
    
    const hasAnalysisComponents = useMemo(() => {
        return dashboard.components.some(component => component.object_type === 'ANALYSIS');
    }, [dashboard.components]);



    const connectionErrors = useMemo(() => {
        if (!analyses.data ) {
            return {};
        }
        // We are looking for components that include metrics that are not connected to a dimension used for filtering, or the date dimension
        const dimensionPipelineNodeIdsInFilters: string[] = [];

        const dimensionIdsInFilters: string[] = [];
        dashboard.components.filter(c => c.object_type === 'FILTER').forEach(c => {
            if (c.filter_config && c.filter_config.filter_type == 'DIMENSION' && c.filter_config.dimension_id) {
                dimensionIdsInFilters.push(c.filter_config.dimension_id);
                const dimension = dimensionsById[c.filter_config.dimension_id];
                if (dimension) {
                    dimensionPipelineNodeIdsInFilters.push(dimension.pipeline_node_id);
                }
            }
        });


        const errorsByComponentId: {[key: string]: string[] } = {};
        dashboard.components.forEach(component => {
            if (component.object_type === 'ANALYSIS') {
                const errors: string[] = [];
                const analysis = analyses.data.find(a => a.id === component.object_id);
                if (analysis) {
                    let measuresToConsider: string[] = [];
                    analysis.measure_ids.forEach(mid => {
                        const measure = measuresById[mid];
                        if (!measure) {
                            errors.push(`Metric with ID ${mid} does not exist.`);
                            return;
                        }
                        if (measure.is_calculation) {
                            const terminators = getUpstreamTerminators(measureDependencyTree, mid);
                            terminators.forEach(t => {
                                measuresToConsider.push(t);
                            });
                        } else {
                            measuresToConsider.push(mid);
                        }
                    })
                    measuresToConsider.forEach(mid => {
                        const measure = measuresById[mid];
                        if (measure) {
                            if (!measure.dimension_join_trees) {
                                errors.push(`Metric "${measure.name}" cannot be broken down by any dimensions.`)
                            } else {
                                const measureDimensions = measure.dimension_join_trees.filter(d => !d.ignore).map(d => d.pipeline_node_id);
                                
                                // Are there dimension pipeline node IDs used in filters that are not connected to this measure?
                                const missingDimensions = dimensionPipelineNodeIdsInFilters.filter(d => !measureDimensions.includes(d));
                                if (missingDimensions.length > 0) {
                                    // These are the pipeline node IDs that are in filters but cannot be connected. For the error message,
                                    // We need to get the name of the dimensions that are actually used in filters
                                    missingDimensions.map(d => dimensionIdsByPipelineNodeId[d]).flat().filter(did => dimensionIdsInFilters.includes(did)).forEach(did => {
                                        const dimName = dimensionsById[did].name;
                                        errors.push(`Metric "${measure.name}" cannot be broken down by "${dimName}"`);
                                    });
                                    
                                }
                            }
                            
                        }
                    });
                }
                errorsByComponentId[component.id] = errors;
            }
        });
        return errorsByComponentId;
    }, [dateRangeConfig, dashboard.components, dimensionFilters, measuresById, analyses.dataUpdatedAt, measureDependencyTree, dimensionsById, dimensionIdsByPipelineNodeId]);


    const [buildOrchestrationID, setBuildOrchestrationID] = useState('');
    const [showBuildModal, setShowBuildModal] = useState(false);

    const refreshComponentData = useCallback(async () => {
        if (!analyses.data) {
            return;
        }
        setDirtyFilters(true);
        setUpdatingDimensionFilters(true);
        setDimensionFiltersForViz(dimensionFilters);
        
        try {
            const analysesToUpdate = dashboard.components
                .filter(c => c.object_type == 'ANALYSIS')
                .map(c => analyses.data.find(a => a.id === c.object_id))
                .filter(a => a !== undefined) as Analysis[];

            const skipDateRangeAnalyses = dashboard.components
                .filter(c => c.object_type == 'ANALYSIS' && c.ignore_date_range)
                .map(c => c.object_id);

            if (analysesToUpdate.length > 0) {
                const prepResult = await AnalysisORM.prepAnalyses(
                    analysesToUpdate,
                    dimensionFilters,
                    dateRangeConfig,
                    skipDateRangeAnalyses
                );
                setShowBuildModal(true);
                setBuildOrchestrationID(prepResult.id as string);
            } else {
                setUpdatingDimensionFilters(false);
            }
        } catch (err) {
            toast('danger', 'Error', getErrorMessage(err));
            setUpdatingDimensionFilters(false);
            setShowBuildModal(false);
        }
    }, [dashboard.components, analyses.dataUpdatedAt, dimensionFilters, dateRangeConfig]);

    const updateDimensionFilters = useCallback((newFilters: AnalysisDimensionFilter[]) => {
        if (!newFilters || newFilters.length === 0) {
            return;
        }
        // We need to merge the new filters in to the existing filters
        setDimensionFilters(draft => {
            // First, remove any existing filters that match the new filters
            const indicesToRemove: number[] = [];
            draft.forEach((f, idx) => {
                if (newFilters.find(nf => nf.dimension_id === f.dimension_id)) {
                    indicesToRemove.push(idx);
                }
            });
        
            for (let i = indicesToRemove.length - 1; i >= 0; i--) {
                draft.splice(indicesToRemove[i], 1);
            }
        const filtersToAdd = newFilters.filter(filter => 
            filter.value && (!Array.isArray(filter.value) || filter.value.length > 0)
        );
            draft.push(...filtersToAdd);
        });

        // DH-1725 - Update the filter default value
        setDashboard(draft => {
            draft.components.forEach(component => {
                if (component.object_type === 'FILTER' && component.filter_config && component.filter_config.dimension_id) {
                    const matchingFilter = newFilters.find(
                        nf => nf.dimension_id === component.filter_config!.dimension_id
                    );
                    if (matchingFilter) {
                        component.filter_config.default_value = matchingFilter.value;
                    }
                }
            });
        });
        

        setNeedsRefresh(true);
    }, [dashboard.components]);

    const onCompleteBuild = useCallback(() => {
        setShowBuildModal(false);
        // Set the applied date range after build is complete
        setAppliedDateRange(dateRangeConfig);
        queryClient.invalidateQueries(['analysisConfiguration']);
        setTimeout(() => {
            setUpdatingDimensionFilters(false);
        }, 2000);
        setNeedsRefresh(false); // Reset needs refresh when build is complete
    }, [dashboard.components, dateRangeConfig]);

    const toggleFavorite = useCallback(() => {
        if (dashboardId) {
            toggleFavoriteDashboard(dashboardId);
        }
    }, [dashboardId]);

    const [componentIdForErrors, setComponentIdForErrors] = useState('');

    const [showNotificationsModal, setShowNotificationsModal] = useState(false);

    const openNotificationsModal = useCallback(() => {
        setShowNotificationsModal(true);
    }, []);



    return <PageStructure
        pageTitle={isNew ? "New Dashboard" : "Edit Dashboard"}
    >
        {buildOrchestrationID && <>
            <BuildModal
                show={showBuildModal}
                orchestrationId={buildOrchestrationID}
                onClose={() => {
                    setShowBuildModal(false);
                }}
                skipInvalidate
                onComplete={onCompleteBuild}
                nonTechnical
                onFailure={(err) => {
                    setShowBuildModal(false);
                    setUpdatingDimensionFilters(false);
                }}
            />
        </>}
        <Modal show={showNotificationsModal} onHide={() => setShowNotificationsModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Dashboard Notifications</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                <DashboardNotificationsEditor
                    dashboardId={dashboard.id as string}
                    currentUser="Admin"
                />
                </Modal.Body>
                <Modal.Footer>

                </Modal.Footer>
        </Modal>
        <Modal size="lg" show={!!componentIdForErrors} onHide={() => setComponentIdForErrors('')}>
            <Modal.Header>
                <Modal.Title>Errors</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Warning>You have filters on this dashboard that are not compatible with the metrics in this component, which will lead to incorrect or misleading data. You can fix this by connecting the metrics to the relevant dimensions on the metric details screen, or by removing the problematic filters or components from your dashboard.</Warning>
                <ul>
                    {connectionErrors[componentIdForErrors]?.map((error, idx) => (
                        <li key={idx}>{error}</li>
                    ))}
                </ul>
                
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-light" onClick={() => setComponentIdForErrors('')}>Close</button>
            </Modal.Footer>
        </Modal>
        
        
        <Offcanvas placement="end" show={showDrawer} onHide={() => setShowDrawer(false)}>
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Edit Dashboard Component</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <Pane>
                    <PaneContent>
                        <div className="p-2">
                            {!!activeComponent &&
                                <ComponentForm 
                                    filterColumnOptions={filterColumnOptions}
                                    component={activeComponent}
                                    onSave={saveActiveComponent}
                                    onDelete={() => {
                                        deleteComponent(activeComponent.id)
                                    }}
                                />
                            }
                        </div>
                    </PaneContent>
                </Pane>
            </Offcanvas.Body>
        </Offcanvas>
        
        {/* <PageSidebar>
            <Pane>
                <PaneContent>
                    <div className="p-3">
                        <RecordTitleAndDescription
                            title={dashboard.name}
                            description={dashboard.description}
                            onChangeTitle={changeName}
                            onChangeDescription={changeDescription}
                        >
                            <Badge pill bg="primary">
                                <i className="mdi mdi-view-dashboard"></i> Dashboard
                            </Badge>
                        </RecordTitleAndDescription>                        
                    </div>
                </PaneContent>
            </Pane>
        </PageSidebar> */}
        <PageContent>
            <PageContentHeader>
                <div className="d-flex center-vertically">
                    {dashboard.id && (
                        <FavoriteButton 
                            isFavorite={isFavorite}
                            onToggle={toggleFavorite}
                            className="me-2"
                        />
                    )}
                    <div className="flex-1">
                        <h1 className="mb-0">
                            <DraftOnly>
                                <EditableText
                                    value={dashboard.name}
                                    onChange={changeName}
                                    emptyPlaceholder="Enter dashboard name..."
                                />
                            </DraftOnly>
                            <ProdOnly>
                                {dashboard.name || '(Unnamed Dashboard)'}
                            </ProdOnly>
                        </h1>
                        <div>
                            <DraftOnly>
                                <EditableText
                                    value={dashboard.description}
                                    onChange={changeDescription}
                                    textarea
                                    emptyPlaceholder="Enter dashboard description..."
                                />
                            </DraftOnly>
                            <ProdOnly>
                                {dashboard.description || 'No description'}
                            </ProdOnly>
                        </div>
                    </div>
                    {hasAnalysisComponents && (
                        <div className="me-3">
                            <DateRangeSelector
                                dateRange={dateRangeConfig?.date_range}
                                customDateRangeStart={dateRangeConfig?.custom_date_range_start}
                                customDateRangeEnd={dateRangeConfig?.custom_date_range_end}
                                onDateRangeChange={(newVal) => {
                                    setDateRangeConfig(draft => ({
                                        ...draft,
                                        date_range: newVal
                                    }));
                                    setNeedsRefresh(true);
                                }}
                                onCustomStartChange={(newVal) => {
                                    setDateRangeConfig(draft => ({
                                        ...draft,
                                        custom_date_range_start: newVal
                                    }));
                                    setNeedsRefresh(true);
                                }}
                                onCustomEndChange={(newVal) => {
                                    setDateRangeConfig(draft => ({
                                        ...draft,
                                        custom_date_range_end: newVal
                                    }));
                                    setNeedsRefresh(true);
                                }}
                                compact={true}
                            />
                        </div>
                    )}
                    
                    <DraftOnly>
                        <button className="btn btn-primary me-1" onClick={addItem}>
                            <i className="mdi mdi-plus-thick"></i> Add Component
                        </button>
                    </DraftOnly>
                    <button 
                        className={`me-1 btn ${needsRefresh ? 'btn-pliable' : 'btn-light'}`} 
                        onClick={refreshComponentData} 
                        disabled={updatingDimensionFilters}
                    >
                        <i className={`mdi mdi-refresh ${updatingDimensionFilters ? 'mdi-spin': ''}`}></i> Reload Data
                    </button>
                    <DraftOnly>
                        <button className="btn btn-danger me-1" onClick={deleteDashboard}>
                            <i className="mdi mdi-delete"></i> Delete
                        </button>
                        <SaveButton onClick={save}/>
                    </DraftOnly>

                    <BSDropdown>
                        <BSDropdown.Toggle variant="light" className="nocaret ps-1 pe-1">
                            <i className="mdi mdi-dots-vertical"></i>
                        </BSDropdown.Toggle>

                        <BSDropdown.Menu>
                            <BSDropdown.Item onClick={openNotificationsModal}>
                                <i className="mdi mdi-content-copy"></i> Notifications
                            </BSDropdown.Item>
                        </BSDropdown.Menu>
                    </BSDropdown>
                    
                </div>
            </PageContentHeader>
            <PageContentInner hasHeader>
                {(!dashboard.components || dashboard.components.length === 0) && <div className="empty-state">
                    <div className="text-center">
                        <h3>Dashboard Builder</h3>
                        <p>Add components to your dashboard to start building. Any smart filters you add will automatically affect each component on your dashboard - no need for additional configuration!</p>
                        <button className="btn btn-primary" onClick={addItem}>
                            <i className="mdi mdi-plus-thick"></i> Add Component
                        </button>
                    </div>
                </div>}
                <div className="">
                    <GridLayout 
                        className="layout" 
                        cols={12} 
                        rowHeight={30} 
                        width={1500} 
                        draggableCancel=".nodrag" 
                        draggableHandle=".drag" 
                        onLayoutChange={handleLayoutChange}
                        isDraggable={inDraftMode}
                        isResizable={inDraftMode}
                    >
                        {dashboard.components.map(gd => {
                            return <div key={gd.id} data-grid={{
                                x: gd.x,
                                y: gd.y,
                                w: gd.width,
                                h: gd.height,
                                minW: 2,
                                minH: 2,
                            }} style={{zIndex: (gd.object_type == 'FILTER') ? '1': '0'}}>
                                <GridItemContainer 
                                    isReady={isReady}
                                    component={gd}
                                    dateRangeConfig={appliedDateRange}
                                    dimensionFilters={dimensionFiltersForViz}
                                    loadingDimensionFilters={updatingDimensionFilters}
                                    mode={mode}
                                    onEdit={() => {
                                        editComponent(gd)
                                    }}
                                    onChangeFilters={(newFilters) => {
                                        handleFilterChange(gd.id, newFilters);
                                    }}
                                    onChangeDimensionFilters={updateDimensionFilters}
                                    
                                    filters={filtersByComponentId[gd.id]}
                                    onDelete={() => {
                                        deleteComponent(gd.id);
                                    }}
                                    errors={connectionErrors[gd.id]}
                                    onShowErrors={() => setComponentIdForErrors(gd.id)}
                                />
                            </div>
                        })}
                        
                    </GridLayout>
                </div>
        
            </PageContentInner>
            
        </PageContent>
    </PageStructure>
}

export default DashboardEditorPage;
